<template>
  <div class="card mt-3 workflow-actions" data-testid="workflow-actions">
    <div class="card-header">
      <h4 class="mb-0">Acciones del workflow</h4>
      <small class="text-muted">Estado actual: <b>{{ statusLabel }}</b></small>
    </div>
    <div class="card-body">
      <!-- Sin acciones disponibles: ticket cerrado/cancelado/rechazado -->
      <p v-if="isTerminal" class="text-muted mb-0" data-testid="wf-no-actions">
        Este ticket está en un estado terminal ({{ statusLabel }}). No hay acciones disponibles.
      </p>
      <p v-else-if="!hasAnyAction" class="text-muted mb-0" data-testid="wf-no-actions-for-role">
        No hay acciones disponibles para tu rol en el estado actual.
      </p>
      <div v-else class="d-flex flex-wrap" style="gap: 0.5rem;">
        <base-button
          v-if="canAssign"
          type="primary"
          size="sm"
          data-action="assign"
          @click="openAssignModal"
        >Asignar</base-button>
        <base-button
          v-if="canReleaseQa"
          type="info"
          size="sm"
          data-action="release-qa"
          @click="openCommentModal('release-qa', 'Liberar a QA')"
        >Liberar a QA</base-button>
        <base-button
          v-if="canResolve"
          type="success"
          size="sm"
          data-action="resolve"
          @click="openCommentModal('resolve', 'Marcar como resuelto')"
        >Resolver</base-button>
        <base-button
          v-if="canReportIncidence"
          type="warning"
          size="sm"
          data-action="report-incidence"
          @click="openCommentModal('report-incidence', 'Reportar incidencia')"
        >Reportar incidencia</base-button>
        <base-button
          v-if="canRetake"
          type="secondary"
          size="sm"
          data-action="retake"
          @click="openConfirmModal('retake', '¿Retomar esta incidencia?')"
        >Retomar</base-button>
        <base-button
          v-if="canRequestProd"
          type="success"
          size="sm"
          data-action="request-prod"
          @click="openConfirmModal('request-prod', '¿Solicitar paso a producción?')"
        >Solicitar producción</base-button>
        <base-button
          v-if="canConfirmProd"
          type="success"
          size="sm"
          data-action="confirm-prod"
          @click="openCommentModal('confirm-prod', 'Confirmar producción')"
        >Confirmar producción</base-button>
        <base-button
          v-if="canClose"
          type="success"
          size="sm"
          data-action="close"
          @click="openConfirmModal('close', '¿Cerrar el ticket definitivamente?')"
        >Cerrar</base-button>
        <base-button
          v-if="canCancel"
          type="danger"
          size="sm"
          data-action="cancel"
          @click="openConfirmModal('cancel', '¿Cancelar el ticket?')"
        >Cancelar</base-button>
        <base-button
          v-if="canReject"
          type="danger"
          size="sm"
          data-action="reject"
          @click="openConfirmModal('reject', '¿Rechazar el ticket?')"
        >Rechazar</base-button>
      </div>
    </div>

    <!-- Modal Assign: seleccionar developer + comentario opcional -->
    <b-modal ref="modalAssign" hide-footer id="modalAssignAction" title="Asignar ticket">
      <div class="form-group">
        <label>Desarrollador <span class="text-danger">*</span></label>
        <select
          v-model="assignForm.developerId"
          class="form-control"
          data-testid="wf-assign-developer"
        >
          <option :value="null">-- selecciona un desarrollador --</option>
          <option
            v-for="u in developerCandidates"
            :key="u.id"
            :value="u.id"
          >{{ u.firstName }} {{ u.lastName }}<span v-if="u.email"> ({{ u.email }})</span></option>
        </select>
      </div>
      <div class="form-group">
        <label>Comentario (opcional)</label>
        <textarea
          v-model="assignForm.comment"
          class="form-control"
          rows="2"
          data-testid="wf-assign-comment"
          placeholder="Notas para el desarrollador..."
        ></textarea>
      </div>
      <div class="text-right mt-3">
        <base-button
          type="secondary"
          size="sm"
          class="mr-2"
          @click="$refs.modalAssign.hide()"
        >Cancelar</base-button>
        <base-button
          type="primary"
          size="sm"
          :disabled="!assignForm.developerId || submitting"
          :loading="submitting"
          data-testid="wf-assign-submit"
          @click="submitAssign"
        >Asignar</base-button>
      </div>
    </b-modal>

    <!-- Modal Comment: comentario obligatorio (release-qa / report-incidence / confirm-prod) -->
    <b-modal ref="modalComment" hide-footer id="modalCommentAction" :title="commentModalTitle">
      <div class="form-group">
        <label>Comentario <span class="text-danger">*</span></label>
        <textarea
          v-model="commentForm.comment"
          class="form-control"
          rows="4"
          data-testid="wf-comment-input"
          placeholder="Comentario obligatorio para esta acción..."
        ></textarea>
      </div>
      <div class="text-right mt-3">
        <base-button
          type="secondary"
          size="sm"
          class="mr-2"
          @click="$refs.modalComment.hide()"
        >Cancelar</base-button>
        <base-button
          type="primary"
          size="sm"
          :disabled="!commentForm.comment || submitting"
          :loading="submitting"
          data-testid="wf-comment-submit"
          @click="submitComment"
        >Confirmar</base-button>
      </div>
    </b-modal>

    <!-- Modal Confirm: confirmación simple (retake / request-prod / close / cancel / reject) -->
    <b-modal ref="modalConfirm" hide-footer id="modalConfirmAction" title="Confirmar acción">
      <p data-testid="wf-confirm-message">{{ confirmForm.message }}</p>
      <div class="text-right mt-3">
        <base-button
          type="secondary"
          size="sm"
          class="mr-2"
          @click="$refs.modalConfirm.hide()"
        >Cancelar</base-button>
        <base-button
          type="primary"
          size="sm"
          :disabled="submitting"
          :loading="submitting"
          data-testid="wf-confirm-submit"
          @click="submitConfirm"
        >Confirmar</base-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import API from '@/services/Api';

// Status IDs del workflow M01 (ver Backend/src/App.DTO/Enumerations.cs::TicketStatuses).
// Duplicados a proposito en el cliente para checks locales; la fuente de verdad sigue
// siendo el backend (ExecuteTransitionAsync valida from/roles/comentario).
const STATUS = Object.freeze({
  NUEVO: 22,
  EN_PROGRESO: 23,
  EN_QA: 24,
  INCIDENCIA_REPORTADA: 25,
  APROBADO_PROD: 26,
  LISTO_CIERRE: 27,
  CERRADO: 28,
  CANCELADO: 9,
  RECHAZADO: 11,
});

const STATUS_LABEL = {
  22: 'Nuevo',
  23: 'En progreso',
  24: 'En QA',
  25: 'Incidencia reportada',
  26: 'Aprobado para producción',
  27: 'Listo para cierre',
  28: 'Cerrado',
  9: 'Cancelado',
  11: 'Rechazado',
};

export default {
  name: 'WorkflowActions',
  props: {
    // El ticket con al menos { id, ticketStatusId, createdById, assignedDeveloperId }
    ticket: { type: Object, required: true },
    // Usuario logueado con al menos { id, roleName }
    currentUser: { type: Object, default: null },
    // Lista de usuarios para dropdown de developer en Assign
    users: { type: Array, default: () => [] },
  },
  data() {
    return {
      submitting: false,
      assignForm: { developerId: null, comment: '' },
      commentForm: { action: null, comment: '' },
      commentModalTitle: '',
      confirmForm: { action: null, message: '' },
    };
  },
  computed: {
    status() {
      return this.ticket && this.ticket.ticketStatusId ? this.ticket.ticketStatusId : 0;
    },
    statusLabel() {
      return STATUS_LABEL[this.status] || 'Desconocido';
    },
    role() {
      return (this.currentUser && this.currentUser.roleName) || '';
    },
    userId() {
      return (this.currentUser && this.currentUser.id) || 0;
    },
    isTerminal() {
      return [STATUS.CERRADO, STATUS.CANCELADO, STATUS.RECHAZADO].includes(this.status);
    },
    isCreator() {
      return this.ticket && this.ticket.createdById === this.userId;
    },
    isBaOrAdmin() {
      return ['BA', 'Administrador'].includes(this.role);
    },
    isAgentOrAdmin() {
      return ['Agente', 'Administrador'].includes(this.role);
    },
    isAgentBaOrAdmin() {
      return ['Agente', 'BA', 'Administrador'].includes(this.role);
    },
    isClient() {
      return this.role === 'Cliente';
    },
    // Workflow del ticket. Retrocompat: si el backend no lo devuelve, asumimos NUEVO
    // (M01 completo) para no romper la UI de tickets pre-Fase 3.
    workflowCode() {
      return (this.ticket && this.ticket.workflowCode) || 'NUEVO';
    },
    isSimple() {
      return this.workflowCode === 'SIMPLE';
    },
    isNuevo() {
      return this.workflowCode === 'NUEVO';
    },
    canAssign() {
      // Aplica en ambos workflows (NUEVO y SIMPLE).
      return this.status === STATUS.NUEVO && this.isBaOrAdmin;
    },
    canReleaseQa() {
      // Sólo NUEVO. En Simple no hay fase EnQA — el agente hace Resolve directo.
      return this.isNuevo && this.status === STATUS.EN_PROGRESO && this.isAgentOrAdmin;
    },
    canResolve() {
      // Sólo SIMPLE. Del EnProgreso pasa directo a ListoCierre (sin QA/prod).
      return this.isSimple && this.status === STATUS.EN_PROGRESO && this.isAgentOrAdmin;
    },
    canReportIncidence() {
      // Sólo NUEVO. Client-only (creador) — Simple no tiene EnQA.
      return this.isNuevo && this.status === STATUS.EN_QA && this.isClient && this.isCreator;
    },
    canRetake() {
      // Sólo NUEVO. Simple no tiene IncidenciaReportada.
      return this.isNuevo && this.status === STATUS.INCIDENCIA_REPORTADA && this.isAgentBaOrAdmin;
    },
    canRequestProd() {
      // Sólo NUEVO. Client-only, solo el creador.
      return this.isNuevo && this.status === STATUS.EN_QA && this.isClient && this.isCreator;
    },
    canConfirmProd() {
      // Sólo NUEVO. Simple no tiene AprobadoProduccion.
      return this.isNuevo && this.status === STATUS.APROBADO_PROD && this.isAgentOrAdmin;
    },
    canClose() {
      // Aplica en ambos: creador cierra desde ListoCierre.
      return this.status === STATUS.LISTO_CIERRE && this.isClient && this.isCreator;
    },
    canCancel() {
      // Estados no-terminales cambian según workflow (Simple no tiene los de QA/prod).
      const nonTerminal = this.isSimple
        ? [STATUS.NUEVO, STATUS.EN_PROGRESO, STATUS.LISTO_CIERRE]
        : [
            STATUS.NUEVO, STATUS.EN_PROGRESO, STATUS.EN_QA,
            STATUS.INCIDENCIA_REPORTADA, STATUS.APROBADO_PROD, STATUS.LISTO_CIERRE,
          ];
      return nonTerminal.includes(this.status) && this.isClient && this.isCreator;
    },
    canReject() {
      // NUEVO permite Reject desde Nuevo o EnProgreso; SIMPLE sólo desde Nuevo
      // (regla del RFC: si ya arrancó trabajo se cancela, no se rechaza).
      const allowedStates = this.isSimple
        ? [STATUS.NUEVO]
        : [STATUS.NUEVO, STATUS.EN_PROGRESO];
      return allowedStates.includes(this.status) && this.isBaOrAdmin;
    },
    hasAnyAction() {
      return this.canAssign || this.canReleaseQa || this.canResolve || this.canReportIncidence
        || this.canRetake || this.canRequestProd || this.canConfirmProd
        || this.canClose || this.canCancel || this.canReject;
    },
    developerCandidates() {
      // El backend acepta cualquier userId > 0 como developer (RequiresDeveloper
      // solo checa != null). El filtro aquí es UX para no ofrecer Clientes.
      //
      // `UserAccount` (DTO) expone `roles: List<int>` (role IDs, NO strings).
      // Cliente = rol 3. Requerimos `roles` explícitamente — sin roles, no
      // mostramos al user (evita asignar un ticket a un Cliente por error si
      // el backend no valida el rol del developer).
      if (!Array.isArray(this.users)) return [];
      return this.users.filter(u => {
        if (!u || !u.id) return false;
        if (!Array.isArray(u.roles) || u.roles.length === 0) return false;
        return !u.roles.includes(3); // 3 = Cliente
      });
    },
  },
  methods: {
    openAssignModal() {
      this.assignForm = { developerId: null, comment: '' };
      this.$refs.modalAssign.show();
    },
    openCommentModal(action, title) {
      this.commentForm = { action, comment: '' };
      this.commentModalTitle = title;
      this.$refs.modalComment.show();
    },
    openConfirmModal(action, message) {
      this.confirmForm = { action, message };
      this.$refs.modalConfirm.show();
    },
    async execAction(action, body) {
      // Endpoints POST /api/Ticket/{id}/{action}. Body opcional; los que no lo requieren
      // aceptan null (los que hacen [FromBody(EmptyBodyBehavior.Allow)]).
      // ResultCodes: Success=1, Alert=2, Warning=3, Fatal=4, Unauthorized=5.
      // Nota: TicketController esta marcado [ApiController], asi que un body con
      // forma invalida (tipos/propiedades incorrectas) SI puede gatillar 400 por
      // model binding — eso cae en el catch de abajo (no es siempre "error de red").
      this.submitting = true;
      try {
        const resp = await API.post(`Ticket/${this.ticket.id}/${action}`, body || null);
        const rc = resp && resp.data ? resp.data.resultCode : null;
        if (rc === 1) {
          this.closeAllModals();
          this.$emit('onCompleted', true);
          return true;
        } else {
          const msg = (resp && resp.data && resp.data.resultMessages && resp.data.resultMessages[0])
            || 'No se pudo ejecutar la acción.';
          this.$notify({ type: 'danger', message: msg });
          return false;
        }
      } catch (e) {
        // Distinguir HTTP errors (401 sesion expirada, 400 body invalido, 500 backend)
        // de errores de red reales (sin `response` = fetch fallo sin llegar al server).
        const status = e && e.response ? e.response.status : null;
        let msg;
        if (status === 401) {
          msg = 'Tu sesión expiró. Vuelve a iniciar sesión.';
        } else if (status && status >= 400) {
          msg = `Error ${status} al ejecutar la acción.`;
        } else {
          msg = 'Error de red al ejecutar la acción. Revisa tu conexión.';
        }
        this.$notify({ type: 'danger', message: msg });
        return false;
      } finally {
        this.submitting = false;
      }
    },
    closeAllModals() {
      if (this.$refs.modalAssign) this.$refs.modalAssign.hide();
      if (this.$refs.modalComment) this.$refs.modalComment.hide();
      if (this.$refs.modalConfirm) this.$refs.modalConfirm.hide();
    },
    submitAssign() {
      if (!this.assignForm.developerId) return;
      // El backend espera `developerId` (TicketActionBody.DeveloperId) en el JSON,
      // NO `assignedDeveloperId`. Si se envia el nombre incorrecto, el model binding
      // deja `DeveloperId=null` y la validacion `RequiresDeveloper` rechaza la transicion.
      this.execAction('assign', {
        developerId: this.assignForm.developerId,
        comment: this.assignForm.comment || null,
      });
    },
    submitComment() {
      if (!this.commentForm.comment) return;
      this.execAction(this.commentForm.action, { comment: this.commentForm.comment });
    },
    submitConfirm() {
      this.execAction(this.confirmForm.action, null);
    },
  },
};
</script>

<style lang="scss" scoped>
.workflow-actions {
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }
}
</style>
